import { type WorkflowEvent } from '@/services/workflow/get-run-events';

export type ExportFormat = 'json' | 'markdown';

interface JsonExportEvent {
  id: string;
  timestamp: string;
  node_id: string | null;
  node_name: string;
  event_type: string;
  event_data: Record<string, unknown>;
}

function resolveNodeName(
  nodeId: string | null,
  displayNameMap: Record<string, string>,
): string {
  if (!nodeId) return 'Unknown Node';
  return displayNameMap[nodeId] ?? nodeId;
}

function buildJsonBlob(
  events: WorkflowEvent[],
  displayNameMap: Record<string, string>,
): Blob {
  const exportEvents: JsonExportEvent[] = events.map((evt) => ({
    id: evt.id,
    timestamp: evt.timestamp,
    node_id: evt.node_instance_id,
    node_name: resolveNodeName(evt.node_instance_id, displayNameMap),
    event_type: evt.event_type,
    event_data: (evt.event_data ?? {}) as Record<string, unknown>,
  }));

  return new Blob([JSON.stringify(exportEvents, null, 2)], {
    type: 'application/json',
  });
}

function renderEventMarkdown(evt: WorkflowEvent, nodeName: string): string {
  const data = (evt.event_data ?? {}) as Record<string, unknown>;
  const ts = new Date(evt.timestamp).toLocaleTimeString();
  const header = `### [${nodeName}] ${ts}`;

  switch (evt.event_type) {
    case 'thinking': {
      const thought = typeof data.thought === 'string' ? data.thought : '';
      const truncated = data.truncated === true ? ' *(truncated)*' : '';
      return `${header} — Thinking${truncated}\n\n${thought}`;
    }
    case 'agent_message': {
      const message = typeof data.message === 'string' ? data.message : '';
      const truncated = data.truncated === true ? ' *(truncated)*' : '';
      return `${header} — Agent Response${truncated}\n\n${message}`;
    }
    case 'tool_call': {
      const toolName =
        typeof data.tool_name === 'string' ? data.tool_name : 'unknown';
      const args =
        typeof data.tool_arguments === 'string'
          ? data.tool_arguments
          : JSON.stringify(data.tool_arguments ?? {}, null, 2);
      return `${header} — Tool Call: \`${toolName}\`\n\n\`\`\`json\n${args}\n\`\`\``;
    }
    case 'tool_result': {
      const toolName =
        typeof data.tool_name === 'string' ? data.tool_name : 'unknown';
      const result =
        typeof data.tool_result === 'string'
          ? data.tool_result
          : JSON.stringify(data.tool_result ?? '', null, 2);
      return `${header} — Tool Result: \`${toolName}\`\n\n${result}`;
    }
    default:
      return `${header} — ${evt.event_type}\n\n${JSON.stringify(data, null, 2)}`;
  }
}

function buildMarkdownBlob(
  events: WorkflowEvent[],
  displayNameMap: Record<string, string>,
): Blob {
  const sections = events.map((evt) => {
    const nodeName = resolveNodeName(evt.node_instance_id, displayNameMap);
    return renderEventMarkdown(evt, nodeName);
  });

  const content = `# Workflow Run Export\n\n---\n\n${sections.join('\n\n---\n\n')}`;
  return new Blob([content], { type: 'text/markdown' });
}

/**
 * Produces a downloadable Blob from a flat, timestamp-ordered list of
 * WorkflowEvents.
 *
 * @param events - Events in ascending timestamp order (as returned by the API)
 * @param displayNameMap - Maps node_instance_id → human-readable node name
 * @param format - Output format: ``"json"`` or ``"markdown"``
 */
export function formatRunExport(
  events: WorkflowEvent[],
  displayNameMap: Record<string, string>,
  format: ExportFormat,
): Blob {
  if (format === 'markdown') {
    return buildMarkdownBlob(events, displayNameMap);
  }
  return buildJsonBlob(events, displayNameMap);
}
